import type { Category, Todo } from "../../domain/entities.ts";
import type { SavedView } from "../../domain/app-types.ts";
import { buildCommandCatalog, type CommandCatalogState } from "./catalog.ts";
import { openCommandPalette, type CommandPaletteItem } from "./controller.ts";

export interface CommandWorkflowCapabilities<Snapshot, Field> {
  state(): CommandCatalogState;
  load?(): Promise<Pick<CommandCatalogState, "tasks" | "categories">>;
  loadError?(): void;
  newTask(): void | Promise<void>;
  quickNotes(createNew: boolean): void | Promise<void>;
  dailyNote?(): void | Promise<void>;
  browserCapture?(): void | Promise<void>;
  openView(view: "calendar" | "backlog" | "cycle"): void | Promise<void>;
  manageCycles(): void | Promise<void>;
  advancedFilters(): void | Promise<void>;
  createTemplate(): void | Promise<void>;
  dataManagement(): void | Promise<void>;
  notifications(): void | Promise<void>;
  editSelected(): void | Promise<void>;
  mutate(id: string, update: (todo: Todo) => void): Promise<Todo | undefined>;
  snapshot(todo: Todo): Snapshot;
  undo(message: string, rootId: string, snapshots: Snapshot[], fields: Field[]): void;
  render(): Promise<unknown>;
  sync(): void;
  openRecentTask(id: string): void | Promise<void>;
  openTask(id: string): void | Promise<void>;
  switchRoot(id: string): void | Promise<void>;
  openCategory(category: Category): void | Promise<void>;
  openSavedView(view: SavedView): void | Promise<void>;
  createFromTemplate(id: string): void | Promise<void>;
  icon(name: string): string;
  discover?(): CommandPaletteItem[];
  palette?: typeof openCommandPalette;
}

export function createCommandWorkflow<Snapshot, Field>(capabilities: CommandWorkflowCapabilities<Snapshot, Field>) {
  async function toggleSelectedComplete(): Promise<void> {
    const selected = capabilities.state().selectedTask;
    if (!selected || capabilities.state().canEditSelected === false) return;
    const before = capabilities.snapshot(selected);
    const wasCompleted = selected.completed;
    await capabilities.mutate(selected.id, todo => { todo.completed = !todo.completed; todo.completedAt = todo.completed ? Date.now() : null; });
    await capabilities.render();
    capabilities.undo(wasCompleted ? "Task reopened" : "Task completed", selected.rootId, [before], ["completed", "completedAt"] as Field[]);
    capabilities.sync();
  }

  async function toggleSelectedPin(): Promise<void> {
    const selected = capabilities.state().selectedTask;
    if (!selected || capabilities.state().canEditSelected === false) return;
    const before = capabilities.snapshot(selected);
    const wasPinned = selected.pinned;
    await capabilities.mutate(selected.id, todo => { todo.pinned = !todo.pinned; });
    await capabilities.render();
    capabilities.undo(wasPinned ? "Task unpinned" : "Task pinned", selected.rootId, [before], ["pinned"] as Field[]);
    capabilities.sync();
  }

  async function open(): Promise<void> {
    let fresh: Pick<CommandCatalogState, "tasks" | "categories"> | undefined;
    try { fresh = capabilities.load ? await capabilities.load() : undefined; }
    catch { capabilities.loadError?.(); return; }
    const commands = buildCommandCatalog({ ...capabilities.state(), ...fresh }, {
      newTask: capabilities.newTask,
      quickNotes: capabilities.quickNotes,
      dailyNote: capabilities.dailyNote,
      browserCapture: capabilities.browserCapture,
      openView: capabilities.openView,
      manageCycles: capabilities.manageCycles,
      advancedFilters: capabilities.advancedFilters,
      createTemplate: capabilities.createTemplate,
      dataManagement: capabilities.dataManagement,
      notifications: capabilities.notifications,
      editSelected: capabilities.editSelected,
      toggleSelectedComplete,
      toggleSelectedPin,
      openRecentTask: capabilities.openRecentTask,
      openTask: capabilities.openTask,
      switchRoot: capabilities.switchRoot,
      openCategory: capabilities.openCategory,
      openSavedView: capabilities.openSavedView,
      createFromTemplate: capabilities.createFromTemplate,
      discover: capabilities.discover,
    });
    (capabilities.palette || openCommandPalette)(commands, { icon: capabilities.icon });
  }

  return { open, toggleSelectedComplete, toggleSelectedPin };
}
